/**
 * Marketing kit — reusable building blocks for the public use-case and
 * integration landing pages.
 *
 * These are deliberately presentational and server-component-friendly: no
 * React hooks, no `useT()`. The new marketing pages are English-only (wrapped
 * in <MarketingShell>, which pins the locale to English), so copy is passed
 * in as plain props rather than going through i18n. Styling reuses the
 * existing design-system tokens (bg-*, line-*, fg-*, brand-*, status-*) and
 * the same visual grammar as <LandingPage> so these pages feel native.
 *
 * Composition: a server page exports `metadata` and renders
 *   <MarketingShell><UseCaseHero/> <WorkflowSteps/> … <CtaBanner/></MarketingShell>
 *
 * <Reveal> (a client component) is used inside these for the same scroll-fade
 * as the homepage; rendering a client child from a server module is fine.
 */

import Link from "next/link";
import Image from "next/image";
import { ArrowRight, CheckCircle2, MinusCircle, AlertTriangle } from "lucide-react";
import { Reveal } from "@/components/reveal";

/* -------------------------------------------------------------------------- */
/*  PRIMITIVES                                                                 */
/* -------------------------------------------------------------------------- */

export function Eyebrow({ children }: { children: React.ReactNode }) {
  return (
    <span className="inline-flex items-center gap-2 rounded-full border border-brand-500/25 bg-brand-500/5 px-3 py-1 text-[11px] font-medium uppercase tracking-[0.18em] text-brand-300">
      <span className="h-1.5 w-1.5 rounded-full bg-brand-300" />
      {children}
    </span>
  );
}

export function PrimaryCTA({
  href,
  children,
}: {
  href: string;
  children: React.ReactNode;
}) {
  return (
    <Link
      href={href}
      className="inline-flex items-center gap-1.5 rounded-lg bg-brand-grad px-5 py-3 text-[14px] font-semibold text-white shadow-glow hover:brightness-110 transition"
    >
      {children}
      <ArrowRight size={16} />
    </Link>
  );
}

export function SecondaryCTA({
  href,
  children,
}: {
  href: string;
  children: React.ReactNode;
}) {
  return (
    <Link
      href={href}
      className="inline-flex items-center gap-1.5 rounded-lg border border-line bg-bg-raised px-4 py-2.5 text-[14px] text-fg hover:bg-bg-hover hover:border-line-strong transition"
    >
      {children}
    </Link>
  );
}

export function SectionHeader({
  eyebrow,
  title,
  description,
  align = "center",
}: {
  eyebrow?: string;
  title: React.ReactNode;
  description?: React.ReactNode;
  align?: "left" | "center";
}) {
  return (
    <div className={`max-w-3xl ${align === "center" ? "mx-auto text-center" : ""}`}>
      {eyebrow && (
        <div className="mb-5">
          <Eyebrow>{eyebrow}</Eyebrow>
        </div>
      )}
      <h2 className="text-balance text-3xl md:text-4xl font-semibold tracking-tight text-fg-bright leading-[1.12]">
        {title}
      </h2>
      {description && (
        <p className="mt-5 text-pretty text-[15px] md:text-base leading-relaxed text-fg-dim">
          {description}
        </p>
      )}
    </div>
  );
}

/** Generic full-width section wrapper with consistent vertical rhythm. */
export function Section({
  id,
  children,
  muted = false,
  className = "",
}: {
  id?: string;
  children: React.ReactNode;
  muted?: boolean;
  className?: string;
}) {
  return (
    <section
      id={id}
      className={`border-b border-line-subtle ${muted ? "bg-bg-raised/25" : ""} ${className}`}
    >
      <div className="mx-auto max-w-6xl px-5 sm:px-6 py-20 md:py-28">{children}</div>
    </section>
  );
}

/* -------------------------------------------------------------------------- */
/*  PRODUCT SHOT — a framed, real product screenshot                           */
/* -------------------------------------------------------------------------- */

/**
 * A real product screenshot in a subtle browser frame. Screenshots are captured
 * from the running app on the demo tenant (see scripts/screenshot-marketing.mjs),
 * banner-free — never mocked or staged UI. `alt` (localized) describes the real
 * feature; `label` is the small monospace caption in the frame chrome.
 */
export function ProductShot({
  src,
  alt,
  label,
}: {
  src: string;
  alt: string;
  label?: string;
}) {
  return (
    <Section>
      <Reveal>
        <figure className="mx-auto max-w-4xl overflow-hidden rounded-2xl border border-line-subtle bg-bg-raised shadow-xl">
          <div className="flex items-center gap-2 border-b border-line-subtle px-4 py-2.5">
            <span aria-hidden className="flex gap-1.5">
              <span className="h-2.5 w-2.5 rounded-full bg-line-subtle" />
              <span className="h-2.5 w-2.5 rounded-full bg-line-subtle" />
              <span className="h-2.5 w-2.5 rounded-full bg-line-subtle" />
            </span>
            {label && (
              <span className="ml-1 text-[11px] font-mono uppercase tracking-[0.18em] text-fg-muted">
                {label}
              </span>
            )}
          </div>
          <div className="relative aspect-[16/10] bg-bg-base">
            <Image
              src={src}
              alt={alt}
              fill
              className="object-cover object-top"
              sizes="(max-width: 1024px) 100vw, 900px"
            />
          </div>
        </figure>
      </Reveal>
    </Section>
  );
}

/* -------------------------------------------------------------------------- */
/*  HERO                                                                       */
/* -------------------------------------------------------------------------- */

export function UseCaseHero({
  eyebrow,
  title,
  highlight,
  subtitle,
  primary,
  secondary,
  bullets,
}: {
  eyebrow: string;
  /** Lead of the H1. */
  title: string;
  /** Gradient-highlighted tail of the H1 (optional). */
  highlight?: string;
  subtitle: React.ReactNode;
  primary: { href: string; label: string };
  secondary?: { href: string; label: string };
  /** Short proof chips under the CTAs. */
  bullets?: string[];
}) {
  return (
    <section className="relative overflow-hidden border-b border-line-subtle">
      <div
        aria-hidden
        className="pointer-events-none absolute inset-x-0 -top-40 h-[560px] bg-[radial-gradient(ellipse_at_top,rgba(244,63,142,0.18),transparent_60%)]"
      />
      <div className="relative mx-auto flex max-w-4xl flex-col items-center px-5 pt-16 pb-16 text-center sm:px-6 md:pt-24 md:pb-20">
        <Reveal>
          <Eyebrow>{eyebrow}</Eyebrow>
        </Reveal>
        <Reveal delay={60}>
          <h1 className="mt-6 text-balance text-[34px] font-semibold leading-[1.08] tracking-tight text-fg-bright sm:text-5xl md:text-6xl">
            {title}
            {highlight && (
              <>
                {" "}
                <span className="bg-brand-grad bg-clip-text text-transparent">
                  {highlight}
                </span>
              </>
            )}
          </h1>
        </Reveal>
        <Reveal delay={120}>
          <p className="mt-6 max-w-2xl text-pretty text-lg leading-relaxed text-fg-dim">
            {subtitle}
          </p>
        </Reveal>
        <Reveal delay={180}>
          <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
            <PrimaryCTA href={primary.href}>{primary.label}</PrimaryCTA>
            {secondary && (
              <SecondaryCTA href={secondary.href}>{secondary.label}</SecondaryCTA>
            )}
          </div>
        </Reveal>
        {bullets && bullets.length > 0 && (
          <Reveal delay={240}>
            <div className="mt-9 flex flex-wrap items-center justify-center gap-x-6 gap-y-2 text-[13px] text-fg-dim">
              {bullets.map((b) => (
                <span key={b} className="inline-flex items-center gap-1.5">
                  <CheckCircle2 size={13} className="text-brand-300 flex-shrink-0" />
                  {b}
                </span>
              ))}
            </div>
          </Reveal>
        )}
      </div>
    </section>
  );
}

/* -------------------------------------------------------------------------- */
/*  WORKFLOW STEPS                                                             */
/* -------------------------------------------------------------------------- */

export type Step = {
  icon: React.ReactNode;
  tag: string;
  title: string;
  body: React.ReactNode;
};

/** Numbered, vertically-connected steps — the golden path of a workflow. */
export function WorkflowSteps({ steps }: { steps: Step[] }) {
  return (
    <ol className="mx-auto mt-14 max-w-3xl space-y-3">
      {steps.map((s, i) => (
        <Reveal key={s.tag} delay={i * 60} as="li">
          <div className="flex items-start gap-4 rounded-2xl border border-line-subtle bg-bg-raised p-5">
            <span className="mt-0.5 inline-flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg bg-brand-grad text-white shadow-glow">
              {s.icon}
            </span>
            <div className="min-w-0">
              <div className="flex items-center gap-2">
                <span className="text-[11px] font-mono text-fg-muted">
                  {String(i + 1).padStart(2, "0")}
                </span>
                <span className="text-[10px] font-mono uppercase tracking-[0.16em] text-fg-muted">
                  {s.tag}
                </span>
              </div>
              <div className="mt-1 text-[15px] font-semibold text-fg-bright leading-snug">
                {s.title}
              </div>
              <p className="mt-1.5 text-[13px] text-fg-dim leading-relaxed">{s.body}</p>
            </div>
          </div>
        </Reveal>
      ))}
    </ol>
  );
}

/* -------------------------------------------------------------------------- */
/*  FEATURE / PROOF-POINT GRID                                                 */
/* -------------------------------------------------------------------------- */

export type Feature = {
  icon: React.ReactNode;
  title: string;
  body: React.ReactNode;
};

export function FeatureGrid({
  features,
  columns = 3,
}: {
  features: Feature[];
  columns?: 2 | 3;
}) {
  const cols = columns === 2 ? "md:grid-cols-2" : "md:grid-cols-2 lg:grid-cols-3";
  return (
    <div className={`mt-14 grid grid-cols-1 ${cols} gap-4`}>
      {features.map((f, i) => (
        <Reveal key={f.title} delay={(i % 3) * 70}>
          <div className="h-full rounded-2xl border border-line-subtle bg-bg-raised p-6 hover:border-line transition">
            <span className="inline-flex h-10 w-10 items-center justify-center rounded-lg bg-bg-elev text-brand-300 border border-line-subtle">
              {f.icon}
            </span>
            <h3 className="mt-5 text-[15px] font-semibold text-fg-bright leading-snug">
              {f.title}
            </h3>
            <p className="mt-2 text-[13px] text-fg-dim leading-relaxed">{f.body}</p>
          </div>
        </Reveal>
      ))}
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/*  CAPABILITY GRID — honest, capability-level status                         */
/* -------------------------------------------------------------------------- */

export type CapabilityStatus = "supported" | "conditional" | "unavailable";

export type Capability = {
  name: string;
  status: CapabilityStatus;
  body: React.ReactNode;
};

const CAP_META: Record<
  CapabilityStatus,
  { label: string; icon: React.ReactNode; cls: string }
> = {
  supported: {
    label: "Supported",
    icon: <CheckCircle2 size={13} />,
    cls: "border-status-ok/30 bg-status-ok/10 text-status-ok",
  },
  conditional: {
    label: "Configuration-dependent",
    icon: <AlertTriangle size={13} />,
    cls: "border-status-warning/30 bg-status-warning/10 text-status-warning",
  },
  unavailable: {
    label: "Not available — by design",
    icon: <MinusCircle size={13} />,
    cls: "border-line bg-bg-elev text-fg-muted",
  },
};

/**
 * Capability-level health grid. Deliberately NOT a single green "connected"
 * checkmark — each capability carries its own honest status so we never imply
 * a write path works when only read does.
 */
export function CapabilityGrid({
  capabilities,
  statusLabels,
}: {
  capabilities: Capability[];
  /** Optional translated overrides for the status-chip labels (keyed by
   *  status). Falls back to the English CAP_META label when not provided. */
  statusLabels?: Partial<Record<CapabilityStatus, string>>;
}) {
  return (
    <div className="mt-12 grid grid-cols-1 md:grid-cols-2 gap-3">
      {capabilities.map((c, i) => {
        const meta = CAP_META[c.status];
        const label = statusLabels?.[c.status] ?? meta.label;
        return (
          <Reveal key={c.name} delay={(i % 2) * 70}>
            <div className="h-full rounded-xl border border-line-subtle bg-bg-raised p-5">
              <div className="flex items-start justify-between gap-3">
                <div className="text-[14px] font-semibold text-fg-bright leading-snug">
                  {c.name}
                </div>
                <span
                  className={`inline-flex flex-shrink-0 items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider ${meta.cls}`}
                >
                  {meta.icon}
                  {label}
                </span>
              </div>
              <p className="mt-2 text-[13px] text-fg-dim leading-relaxed">{c.body}</p>
            </div>
          </Reveal>
        );
      })}
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/*  SECURITY / GOVERNANCE NOTE                                                 */
/* -------------------------------------------------------------------------- */

export function SecurityNote({
  icon,
  title,
  children,
}: {
  icon: React.ReactNode;
  title: string;
  children: React.ReactNode;
}) {
  return (
    <Reveal>
      <div className="mt-12 flex items-start gap-4 rounded-2xl border border-line-subtle bg-bg-raised/60 p-6">
        <span className="mt-0.5 inline-flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg bg-bg-elev text-brand-300 border border-line-subtle">
          {icon}
        </span>
        <div>
          <div className="text-[15px] font-semibold text-fg-bright">{title}</div>
          <div className="mt-2 text-[13px] text-fg-dim leading-relaxed">{children}</div>
        </div>
      </div>
    </Reveal>
  );
}

/* -------------------------------------------------------------------------- */
/*  FAQ — native <details> accordion (no client JS needed)                    */
/* -------------------------------------------------------------------------- */

export type FaqItem = { q: string; a: React.ReactNode };

export function Faq({ items }: { items: FaqItem[] }) {
  return (
    <div className="mx-auto mt-12 max-w-3xl divide-y divide-line-subtle rounded-2xl border border-line-subtle bg-bg-raised">
      {items.map((it) => (
        <details key={it.q} className="group px-5">
          <summary className="flex cursor-pointer list-none items-center justify-between gap-4 py-5 text-[15px] font-medium text-fg-bright">
            {it.q}
            <span className="text-fg-muted transition group-open:rotate-45">
              <span className="block text-xl leading-none">+</span>
            </span>
          </summary>
          <div className="pb-5 -mt-1 text-[13px] text-fg-dim leading-relaxed">
            {it.a}
          </div>
        </details>
      ))}
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/*  RELATED LINKS                                                              */
/* -------------------------------------------------------------------------- */

export type RelatedLink = { href: string; label: string; description: string };

export function RelatedLinks({
  title = "Keep exploring",
  links,
}: {
  title?: string;
  links: RelatedLink[];
}) {
  return (
    <div>
      <div className="text-[11px] font-mono uppercase tracking-[0.18em] text-fg-muted text-center">
        {title}
      </div>
      <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {links.map((l) => (
          <Link
            key={l.href}
            href={l.href}
            className="group rounded-xl border border-line-subtle bg-bg-raised p-5 hover:border-line transition"
          >
            <div className="flex items-center justify-between gap-2 text-[14px] font-semibold text-fg-bright">
              {l.label}
              <ArrowRight
                size={15}
                className="text-fg-muted group-hover:text-brand-300 transition"
              />
            </div>
            <p className="mt-1.5 text-[13px] text-fg-dim leading-relaxed">
              {l.description}
            </p>
          </Link>
        ))}
      </div>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/*  CTA BANNER                                                                 */
/* -------------------------------------------------------------------------- */

export function CtaBanner({
  title,
  description,
  primary,
  secondary,
}: {
  title: string;
  description?: React.ReactNode;
  primary: { href: string; label: string };
  secondary?: { href: string; label: string };
}) {
  return (
    <section className="relative overflow-hidden border-b border-line-subtle">
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_at_center,rgba(244,63,142,0.16),transparent_65%)]"
      />
      <div className="relative mx-auto max-w-3xl px-5 sm:px-6 py-24 md:py-28 text-center">
        <Reveal>
          <h2 className="text-balance text-3xl md:text-5xl font-semibold tracking-tight text-fg-bright">
            {title}
          </h2>
        </Reveal>
        {description && (
          <Reveal delay={100}>
            <p className="mt-5 text-balance text-[15px] md:text-lg text-fg-dim">
              {description}
            </p>
          </Reveal>
        )}
        <Reveal delay={180}>
          <div className="mt-9 flex flex-wrap items-center justify-center gap-3">
            <PrimaryCTA href={primary.href}>{primary.label}</PrimaryCTA>
            {secondary && (
              <SecondaryCTA href={secondary.href}>{secondary.label}</SecondaryCTA>
            )}
          </div>
        </Reveal>
      </div>
    </section>
  );
}
